import { useEffect, useRef, useState } from 'react'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Notice } from '../ui/controls'
import type { MyPhoto } from '../api/types'

/**
 * The primary photo (决议 V1.2 §7, Group 20).
 *
 * Two things this screen has to say plainly, because getting either wrong costs the product a
 * user: the photo is **required** — without an approved one the profile never completes and the
 * account is never matched (§7.3) — and it is **not visible to anyone** until they have unlocked
 * the report (§7.2). People are reasonably wary about a photo on a matchmaking site; the honest
 * answer to that wariness is on the page rather than in a help article.
 */
export function PhotoPage() {
  const { t } = useI18n()
  const fileInput = useRef<HTMLInputElement>(null)

  const [photo, setPhoto] = useState<MyPhoto | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const load = () =>
    api.photo
      .mine()
      .then(setPhoto)
      .catch(() => setPhoto(null))
      .finally(() => setLoading(false))

  useEffect(() => {
    void load()
  }, [])

  const upload = (file: File) => {
    setError(null)
    setBusy(true)

    api.photo
      .upload(file)
      .then(setPhoto)
      .catch(setError)
      .finally(() => {
        setBusy(false)

        // Clearing lets someone pick the same file again after a refusal.
        if (fileInput.current) {
          fileInput.current.value = ''
        }
      })
  }

  if (loading) {
    return <Busy />
  }

  const statusKey = photo ? (`photo.status.${photo.moderationStatus}` as const) : null

  return (
    <div className="page stack">
      <h1>{t('photo.title')}</h1>

      <p className="lede">{t('photo.required')}</p>
      <Notice>{t('photo.notVisible')}</Notice>

      <ErrorBanner error={error} />

      <section className="card">
        {photo ? (
          <div className="photo-row">
            <img className="photo-preview" src={api.photo.url(photo.photoId)} alt="" />
            <div className="stack">
              <span className={`badge badge-${photo.moderationStatus.toLowerCase()}`}>
                {statusKey ? t(statusKey) : photo.moderationStatus}
              </span>
              <span className="field-hint">
                {photo.widthPx} × {photo.heightPx}
              </span>
            </div>
          </div>
        ) : (
          <p>{t('photo.none')}</p>
        )}

        <input
          ref={fileInput}
          className="control"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0]

            if (file) {
              upload(file)
            }
          }}
        />

        <p className="field-hint">{t('photo.limits')}</p>
        <p className="field-hint">{t('photo.stripped')}</p>

        {busy ? <Busy label={t('photo.uploading')} /> : null}

        {photo ? (
          <button
            type="button"
            className="danger"
            disabled={busy}
            onClick={() => {
              setError(null)
              setBusy(true)

              api.photo
                .remove()
                .then(() => setPhoto(null))
                .catch(setError)
                .finally(() => setBusy(false))
            }}
          >
            {t('photo.remove')}
          </button>
        ) : null}
      </section>
    </div>
  )
}
